/**
 * WHAT THE SERVICE WORKER DOES WITH EACH REQUEST — decided here, as pure functions, so the
 * decision is tested without a browser and the worker itself (`../sw.ts`) stays thin wiring.
 *
 * The two planes of the architecture decide the caching, and each rule follows from a property the
 * architecture already guarantees rather than from a caching convention:
 *
 *   - CONTENT is immutable per version — the version is in the URL (`<base>/<version>/<file>`), so a
 *     given URL never means two things. That makes it CACHE-FIRST: once fetched it never needs
 *     revalidation, and a downloaded library reads with no network at all.
 *   - EXCEPT THE BUNDLE, which is the one content object republished AT THE SAME URL. The version
 *     is a digest of the side artifacts and deliberately excludes the bundle, so publishing new
 *     arguments leaves the version unchanged — and pipeline/lib/publish.ts says so where it sets
 *     the header: "the bundle at a given version URL is NOT immutable: it must revalidate". This
 *     worker read the version in the path, concluded immutable, and cached it first. The result is
 *     the worst failure this app can have: publishing keeps succeeding, the edge keeps serving the
 *     new bundle, verify-live keeps agreeing — and a returning reader holds the bundle they first
 *     cached FOREVER, because the URL never changes and cache-first never asks. It was found by a
 *     reader opening Abraham and seeing one argument where the live bundle has ten.
 *     So the bundle is NETWORK-FIRST: fresh whenever there is a network, and the cached copy when
 *     there is not, which is what must-revalidate means on a client that can go offline.
 *   - THE MANIFEST says which version is live, so it is NETWORK-FIRST: a reader online must see a new
 *     build the moment it ships, and a reader offline falls back to the last one they saw.
 *   - THE APP SHELL — this origin's own HTML, JS and CSS — is CACHE-FIRST, because Vite fingerprints
 *     every asset: a cached one is never stale, and a new deploy is simply new URLs.
 *   - EVERYTHING ELSE — the social API, sign-in, LiveKit — is PASSTHROUGH: never cached, because it
 *     is authed, dynamic, or someone else's to decide.
 *
 * The worker is told where content and the manifest live (it cannot guess another origin's URLs);
 * until it is, those requests fall through to the network, which is correct online and the only case
 * the app's own load order makes reachable — see `./register.ts`.
 */
import { contentUrl } from '@versesverse/schema/api';

export const SHELL_CACHE = 'vv-shell-v1';
export const CONTENT_CACHE = 'vv-content-v1';

/*
 * THE COMPILATION IS A THIRD CLASS, AND IT NEEDED ONE — `ARCHITECTURE.md D26`, `DELIVERY.md §9`.
 *
 * It is neither content nor shell. It comes from the API rather than the edge, it is `private` so no
 * shared cache may hold it, and it is answered per view — so it cannot be lumped in with either of the
 * two rules above. Left as `passthrough` (which is where it fell before this) the browser's own HTTP
 * cache would honour `private, immutable` and a returning reader online would be fine — and a reader
 * OFFLINE would have no map at all once the bundle stops carrying the layer, because nothing would
 * have put it anywhere the worker can reach.
 *
 * ONE CACHE PER BUILD, and that is the whole of the version discipline. `vv-compilation-<version>` is
 * a partition, so a build's answers cannot be read under another build's name even by mistake, and
 * retiring a build is deleting one cache rather than walking entries. A miss inside a partition goes to
 * the network; it never falls back to a neighbouring version, which is the failure mode that would
 * draw one build's edges between another build's figures.
 *
 * WHAT THIS COSTS §8, said plainly rather than discovered later: a record served from this cache never
 * reaches the server, so the coverage counter cannot see it. That is not new — the HTTP cache already
 * made it true — and it is the right behaviour for a limit meant to catch extraction, because a caller
 * who wants the whole layer must still fetch every piece of it once. What the counter measures is
 * first contact, and first contact is what breadth is made of.
 */
export const COMPILATION_CACHE_PREFIX = 'vv-compilation-';

/** The cache partition holding one build's compilation. */
export function compilationCacheFor(version: string): string {
  return `${COMPILATION_CACHE_PREFIX}${version}`;
}

/** The caches THIS worker owns; on activate, any other is from an older worker and is dropped. */
export const OWNED_CACHES: readonly string[] = [SHELL_CACHE, CONTENT_CACHE];

/**
 * Whether a cache found on activate belongs to this worker.
 *
 * The compilation's partitions are named per build, so they cannot be listed in advance — without
 * this, every activation would delete the compilation a reader had saved for offline, and the next
 * flight would silently have no map.
 */
export function isOwnedCache(name: string): boolean {
  return OWNED_CACHES.includes(name) || name.startsWith(COMPILATION_CACHE_PREFIX);
}

/**
 * Compilation partitions worth dropping: every build except the one now live.
 *
 * A reader who has not reloaded is still reading the build they opened with, so this runs when a new
 * worker activates rather than the moment a new library publishes — the same reasoning that makes the
 * content cache keyed by version rather than cleared on publish.
 */
export function staleCompilationCaches(names: readonly string[], keepVersion: string): string[] {
  const keep = compilationCacheFor(keepVersion);
  return names.filter(name => name.startsWith(COMPILATION_CACHE_PREFIX) && name !== keep);
}

/**
 * CONTENT HELD FOR A BUILD THE READER IS NO LONGER ON — the entries to drop from `CONTENT_CACHE`.
 *
 * The compilation gets a cache PER BUILD and is cleaned above; content gets ONE cache whose name has
 * never changed, so every build a reader has ever opened stays in it for good. Nothing evicted them,
 * and a saved library is 34 files and 90.7 MB — so a reader who taps "Save for offline" across two
 * builds carries both, and the browser is left to settle it by hitting the origin quota, which it
 * answers by dropping the WHOLE origin. The copy they chose to save is the thing that disappears.
 *
 * These entries are not merely old, they are UNREACHABLE: content is addressed as
 * `<base>/<version>/<file>`, so nothing in the app can ever ask for one of them again.
 *
 * IT REFUSES TO SWEEP A CONTENT BASE ON THE APP'S OWN ORIGIN, and that is the whole of what makes a
 * deletion safe here. `planFor` calls anything under the content base content — an exact prefix of a
 * base the app supplied, which is right when content is a CDN, and which swallows the app's own
 * shell when the two share an origin. Then "under the base and not under the live version" is every
 * asset the reader needs to open the app offline, and this would delete them. Production serves
 * content from its own origin; a deployment that does not gets no sweep rather than a wrong one.
 *
 * The keep-prefix is built with `contentUrl`, the same speller the client fetches through, because
 * the version segment is percent-encoded. A prefix written out by hand here would agree with it for
 * every version needing no encoding — and drop the LIVE build's content for the first one that did.
 */
export function staleContentEntries(urls: readonly string[], ctx: RouteContext): string[] {
  if (!ctx.contentBase || !ctx.version) return [];
  if (ctx.contentBase.startsWith(ctx.selfOrigin)) return [];
  const keep = contentUrl(ctx.contentBase, ctx.version, '');
  const ours = `${ctx.contentBase.replace(/\/+$/, '')}/`;
  return urls.filter(url => url.startsWith(ours) && !url.startsWith(keep));
}

/**
 * Whether a response means a partition should be dropped rather than kept.
 *
 * A **409** from a compilation route is the server saying it no longer serves that build (`§6`), so
 * everything held under that version is unreachable by definition and holding it only delays the
 * reader's reload. Anything else — a 404 for a figure that does not exist, a 500, a network failure —
 * leaves the partition alone: one missing record is not a reason to throw away a saved library.
 */
export function partitionIsGone(status: number): boolean {
  return status === 409;
}

export type Strategy = 'cache-first' | 'network-first' | 'passthrough';
export interface CachePlan {
  strategy: Strategy;
  /** Which cache the strategy reads and writes. Absent only for `passthrough`. */
  cache?: string;
}

/** What the worker knows about where things live — supplied by the app once the manifest is read. */
export interface RouteContext {
  /** The app's own origin — its shell. */
  selfOrigin: string;
  /** Where versioned content lives (`manifest.content.baseUrl`), once known. */
  contentBase?: string;
  /** The exact manifest URL, once known. */
  manifestUrl?: string;
  /**
   * The URL prefix the compilation is answered under, once known (D26).
   *
   * Supplied by the app rather than guessed, exactly as `contentBase` is: this is another origin's URL
   * shape, and a worker that inferred it would be a worker caching whatever else happened to match.
   */
  compilationBase?: string;
  /** The build the reader opened with — which partition the compilation is cached in. */
  version?: string;
}

const PASSTHROUGH: CachePlan = { strategy: 'passthrough' };

/**
 * The one content file republished at an unchanged URL — see the note above and the header
 * pipeline/lib/publish.ts sets on it.
 *
 * Matched on the filename rather than the whole path because the version in front of it is exactly
 * the part that varies, and matched after the query is removed so a cache-busting parameter cannot
 * turn the bundle back into something this worker holds forever.
 */
const BUNDLE_FILE = '/bundle.json';
function isBundle(url: string): boolean {
  const [path] = url.split(/[?#]/);
  return path.endsWith(BUNDLE_FILE);
}

/**
 * How to answer one request. `mode` is the `Request.mode` — `'navigate'` for a page load, which is
 * the case that must return the app shell so the reader gets the app itself while offline.
 */
export function planFor(method: string, url: string, mode: string, ctx: RouteContext): CachePlan {
  if (method !== 'GET') return PASSTHROUGH;

  /* Content first: it is the only cross-origin thing worth holding, and the test is an exact prefix
     of a base the app supplied — not a guess about the shape of someone else's URLs. */
  if (ctx.contentBase && url.startsWith(ctx.contentBase)) {
    return isBundle(url)
      ? { strategy: 'network-first', cache: CONTENT_CACHE }
      : { strategy: 'cache-first', cache: CONTENT_CACHE };
  }
  /*
   * The compilation, into its own per-build partition (D26). Cache-first for the same reason content is
   * — the version is in the URL, so a given URL never means two things — but held apart from content
   * because it is `private`, comes from the API, and must be droppable one build at a time.
   *
   * BOTH the base and the version are required. Without a version there is no partition to name, and
   * caching it in some default would be the cross-version read this design exists to make impossible;
   * passthrough is the honest answer until the app has said which build it opened with.
   */
  if (ctx.compilationBase && ctx.version && url.startsWith(ctx.compilationBase)) {
    return { strategy: 'cache-first', cache: compilationCacheFor(ctx.version) };
  }
  /* The manifest must be fresh when it can be, and usable when it cannot. */
  if (ctx.manifestUrl && sameResource(url, ctx.manifestUrl)) {
    return { strategy: 'network-first', cache: SHELL_CACHE };
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return PASSTHROUGH;
  }
  if (parsed.origin !== ctx.selfOrigin) return PASSTHROUGH;
  /*
   * A NAVIGATION IS NETWORK-FIRST, AND IT WAS CACHE-FIRST UNTIL 2026-08-18 — which meant a reader who
   * opened this site once **never saw another deploy of it**.
   *
   * The reasoning that produced the old line was sound about offline and wrong about freshness. What
   * a navigation returns is `index.html`, and `index.html` is MUTABLE: every build rewrites it to
   * name new fingerprinted assets. Cached cache-first, in a cache called `vv-shell-v1` whose name
   * never changes, with `cacheFirst` doing no revalidation at all, the first visit's copy is the last
   * one a reader ever gets. The new worker activates, claims its clients, deletes every cache it does
   * not own — and then serves the same stale shell, pointing at the same old asset hashes.
   *
   * The generated pages are the second half of the same fault, and the one `tools/assemble-site.ts`
   * prints a warning about on every run: `/n/abraham.html` is a navigation too, so a reader who opened
   * one figure kept that build's copy of it for good.
   *
   * Network-first is the same answer the manifest already gets, for the same reason — fresh when there
   * is a network, and the cached copy when there is not. The offline story is unchanged: `networkFirst`
   * falls back to the cache when the fetch throws, so the app still opens on a plane. What it costs is
   * one request before paint, for a 1.7 kB document.
   *
   * `SHELL_CACHE` is deliberately still the cache: the fallback copy is what makes offline work, and
   * a cache that is written on every successful navigation is exactly the one to fall back to.
   */
  if (mode === 'navigate') return { strategy: 'network-first', cache: SHELL_CACHE };
  /* Fingerprinted: the URL cannot change meaning, so a held copy is never stale. */
  if (isFingerprinted(parsed.pathname)) return { strategy: 'cache-first', cache: SHELL_CACHE };
  /* This origin's own unfingerprinted files — `/style.css` above all. Network-first, because the
     URL stays put while the bytes behind it move on every deploy. See `isFingerprinted`. */
  if (isOwnAsset(parsed.pathname)) return { strategy: 'network-first', cache: SHELL_CACHE };
  return PASSTHROUGH;
}

/** The same resource ignoring the query — the manifest is asked for plainly, but a cache-buster must not miss it. */
function sameResource(a: string, b: string): boolean {
  try {
    const ua = new URL(a);
    const ub = new URL(b);
    return ua.origin === ub.origin && ua.pathname === ub.pathname;
  } catch {
    return a === b;
  }
}

/**
 * A URL THAT CAN NEVER MEAN TWO THINGS — which is what makes cache-first safe, and which this
 * conflated with "an asset" until 2026-08-22.
 *
 * Vite fingerprints everything it emits and puts it in `/assets/` (`index-B4wz6DPT.js`), so that
 * prefix is the whole test. The rule used to be an extension match — any `.css`, `.js`, `.png`,
 * `.svg`, `.webmanifest` or `.ico` anywhere — and its own comment said "fingerprinted", which the
 * files it caught were not: `/style.css`, `/icon.svg`, `/app.webmanifest`, `/og-card.png`.
 *
 * `/style.css` is the one that mattered. It dresses all 2,688 generated pages, its URL never
 * changes, and cache-first never revalidates — so a reader whose worker had cached it kept that
 * copy across every future deploy. Navigations are network-first, deliberately, which makes the
 * failure worse rather than better: FRESH MARKUP WITH STALE RULES. The print work shipped
 * `<p class="printed-from">` and the rule hiding it on screen lives in that stylesheet, so the
 * exact reader this describes gets a raw URL printed under the heading of every page.
 *
 * That is the fault this file already records one branch up — "a reader who opened this site once
 * never saw another deploy" — surviving in the assets after it was fixed for navigations.
 */
function isFingerprinted(pathname: string): boolean {
  return pathname.startsWith('/assets/');
}

/**
 * This origin's own static files, fingerprinted or not.
 *
 * The unfingerprinted ones are network-first into the same cache: fresh whenever there is a
 * network, and the last copy when there is not, which is the answer the manifest and every
 * navigation already get and for the same reason. It costs one revalidation — the header Pages
 * sends for these is `must-revalidate` anyway, so a reader without a worker was already paying it.
 */
function isOwnAsset(pathname: string): boolean {
  return /\.(?:js|css|woff2?|png|svg|webmanifest|ico)$/.test(pathname);
}

/* ---------------- the messages the app sends the worker ---------------- */

export const MSG_CONFIG = 'vv-config';
export const MSG_PREFETCH = 'vv-prefetch';

/** Tells the worker where content and the manifest live, so its cache decisions are exact. */
export interface ConfigMessage {
  type: typeof MSG_CONFIG;
  contentBase: string;
  manifestUrl: string;
  /** Where the compilation is answered from, and which build — see `RouteContext`. Optional so a
      worker and an app of different vintages still agree about content and the manifest. */
  compilationBase?: string;
  version?: string;
}

/** Asks the worker to pull the whole current build into the content cache for offline reading. */
export interface PrefetchMessage {
  type: typeof MSG_PREFETCH;
  files: string[];
}

export type SwMessage = ConfigMessage | PrefetchMessage;

/**
 * A message from the page is untrusted input — anything can `postMessage` a worker — so it is
 * validated into shape rather than cast into it.
 */
export function asSwMessage(data: unknown): SwMessage | undefined {
  if (typeof data !== 'object' || data === null || !('type' in data)) return undefined;
  const record = data as Record<string, unknown>;
  if (record.type === MSG_CONFIG) {
    if (typeof record.contentBase !== 'string' || typeof record.manifestUrl !== 'string') return undefined;
    /* The two D26 fields are taken only when BOTH are strings. A base with no version names no
       partition, and a version with no base matches nothing — either alone is a message the worker
       should ignore rather than half-apply. */
    const pair = typeof record.compilationBase === 'string' && typeof record.version === 'string'
      ? { compilationBase: record.compilationBase, version: record.version }
      : {};
    return { type: MSG_CONFIG, contentBase: record.contentBase, manifestUrl: record.manifestUrl, ...pair };
  }
  if (record.type === MSG_PREFETCH) {
    return Array.isArray(record.files) && record.files.every(f => typeof f === 'string')
      ? { type: MSG_PREFETCH, files: record.files as string[] }
      : undefined;
  }
  return undefined;
}

/**
 * WHAT THE WORKER SAYS WHILE IT IS STILL SAVING.
 *
 * The offline download is 34 files — 90.7 MB into the cache, 23.4 MB over the wire — and the only
 * thing a reader saw for the whole of it was one unchanging label. The counts existed and were
 * posted once, at the end.
 *
 * Declared here beside the two messages that go the other way, so the whole vocabulary between the
 * page and the worker is in one file rather than a string typed on both sides of a port.
 */
export const PREFETCH_PROGRESS = 'vv-prefetch-progress';

/** How far through the list the worker has got. `done` counts attempts, not successes. */
export interface PrefetchProgress {
  type: typeof PREFETCH_PROGRESS;
  done: number;
  total: number;
}
